import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import SignScreen from '../../Signing/screens/SignScreen';
import {fetchSignPageModel, submitSignature} from '../../Signing/api/signingApi';
import {requestSigningSession} from '../api/renewalClickApi';

const styles = {
  loading: {display: 'flex', justifyContent: 'center', p: 4},
  error: {maxWidth: 560, mx: 'auto', px: 2, py: 3},
};

const EmbeddedSigning = ({shortLinkKey, onSigned}) => {
  const intl = useIntl();
  const [document, setDocument] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setError(false);
      setDocument(null);
      try {
        const session = await requestSigningSession(shortLinkKey);
        if (cancelled) return;
        if (session.signed) {
          onSigned();
          return;
        }
        if (session.provider !== 'WobiSign' || !session.signKey) throw new Error('SIGNING_PROVIDER_CHANGED');
        const model = await fetchSignPageModel(session.signKey);
        if (cancelled) return;
        if (model.status === 'SIGNED') {
          onSigned();
          return;
        }
        if (model.status !== 'PENDING' || model.authStatus !== 'AUTHENTICATED' || !model.documentHtml) {
          throw new Error('SIGNING_DOCUMENT_UNAVAILABLE');
        }
        setDocument({signKey: session.signKey, model});
      } catch (err) {
        window.rollbar?.error('Embedded renewal signing failed to load', {shortLinkKey, error: err.message});
        if (!cancelled) setError(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [shortLinkKey, attempt, onSigned]);

  const submit = async (signKey, signatureImage) => {
    // Reading can take longer than the short signing session. Recheck the host authentication and
    // active document before refreshing it, never ask for a second OTP or submit a replaced document.
    const session = await requestSigningSession(shortLinkKey);
    if (session.provider !== 'WobiSign' || session.signKey !== signKey) {
      throw new Error(intl.formatMessage({id: 'renewalClick.signing.sessionError'}));
    }
    if (session.signed) {
      return {status: 'SIGNED'};
    }
    try {
      return await submitSignature(signKey, signatureImage);
    } catch (error) {
      // The document may have been signed even if its HTTP response was lost.
      const current = await requestSigningSession(shortLinkKey);
      if (current.provider === 'WobiSign' && current.signKey === signKey && current.signed) {
        return {status: 'SIGNED'};
      }
      throw error;
    }
  };

  if (error) {
    return (
      <Box sx={styles.error}>
        <Alert severity='error'>{intl.formatMessage({id: 'renewalClick.signing.sessionError'})}</Alert>
        <Button onClick={() => setAttempt((value) => value + 1)}>
          {intl.formatMessage({id: 'renewalClick.error.retry'})}
        </Button>
      </Box>
    );
  }
  if (!document) return <Box sx={styles.loading}><CircularProgress /></Box>;
  return (
    <SignScreen
      key={document.signKey}
      signKey={document.signKey}
      model={document.model}
      showStepper={false}
      onSubmit={submit}
      onSigned={onSigned}
    />
  );
};

EmbeddedSigning.propTypes = {
  shortLinkKey: PropTypes.string.isRequired,
  onSigned: PropTypes.func.isRequired,
};

export default EmbeddedSigning;
